import { Suspense, useState, useEffect, useRef } from 'react';
import { Canvas, useFrame, extend } from '@react-three/fiber';
import {
  Html,
  Float,
  PerspectiveCamera,
  OrbitControls,
  useGLTF,
  Loader,
  useTexture
} from '@react-three/drei';
import * as THREE from 'three';
import { Water } from 'three/examples/jsm/objects/Water';

extend({ Water });
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../store/slices/authSlice';
import api from '../api/config';
import './Login3D.css';

// Ocean Model Component - Agua realista con reflejos
function Ocean() {
  const waterRef = useRef();
  const textureLoader = new THREE.TextureLoader();

  useEffect(() => {
    if (waterRef.current) {
      const water = waterRef.current;

      // Configurar agua con texturas del océano
      water.material.uniforms['size'].value = 1.5;
      water.material.uniforms['distortionScale'].value = 3.0;
      water.material.uniforms['alpha'].value = 0.95;

      // Cargar textura de normal para el agua
      textureLoader.load('/ocean/textures/Scene_-_Root_normal.png', (texture) => {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        water.material.uniforms['normalSampler'].value = texture;
      });
    }
  }, []);

  useFrame((state) => {
    if (waterRef.current) {
      waterRef.current.material.uniforms['time'].value = state.clock.elapsedTime * 0.3;
    }
  });

  return (
    <water
      ref={waterRef}
      args={[
        new THREE.CircleGeometry(150, 256),
        {
          textureWidth: 512,
          textureHeight: 512,
          waterNormals: new THREE.TextureLoader().load(
            '/ocean/textures/Scene_-_Root_normal.png',
            (texture) => {
              texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            }
          ),
          sunDirection: new THREE.Vector3(1, 1, 0.5).normalize(),
          sunColor: 0xffffff,
          waterColor: 0x0055aa,
          distortionScale: 3.0,
          fog: false,
        },
      ]}
      rotation-x={-Math.PI / 2}
      position={[0, -4, 0]}
    />
  );
}

// Paper Boat Model Component
function PaperBoat() {
  const boatRef = useRef();
  const { scene } = useGLTF('/paper_boat.glb');

  useFrame((state) => {
    if (boatRef.current) {
      // Movimiento de balanceo del barco
      boatRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.8) * 0.05;
      boatRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.6) * 0.03;
      // Flotación vertical (mucho más arriba)
      boatRef.current.position.y = 8 + Math.sin(state.clock.elapsedTime * 0.5) * 0.5;
      // Rotación lenta
      boatRef.current.rotation.y += 0.002;
    }
  });

  return (
    <Float
      speed={1.2}
      rotationIntensity={0.1}
      floatIntensity={0.2}
      floatingRange={[0, 0.1]}
    >
      <primitive
        ref={boatRef}
        object={scene.clone()}
        scale={400}
        position={[0, 8, 0]}
      />
    </Float>
  );
}

// Título 3D de la aplicación
function AppTitle() {
  return (
    <Html
      transform
      distanceFactor={10}
      position={[0, 15, -10]}
      center
      style={{
        fontSize: '72px',
        fontWeight: '700',
        color: '#ffffff',
        textShadow: '0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255,255,255,0.4), 2px 2px 4px rgba(0, 0, 0, 0.5)',
        letterSpacing: '5px',
        fontFamily: "'Railway Gank', 'Space Grotesk', sans-serif",
        userSelect: 'none',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase'
      }}
    >
      PAPER BOAT
    </Html>
  );
}

// Formulario de Login 3D
function LoginForm3D({ onLogin, error, loading }) {
  const dispatch = useDispatch(); // Add dispatch hook
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email && password) {
      onLogin({ email, password });
    }
  };

  const handleRecovery = async (e) => {
    e.preventDefault();
    if (!email) {
      setRecoveryError('Por favor ingresa tu correo electrónico');
      return;
    }

    try {
      setRecoveryError('');
      setRecoveryMessage('');
      setRecoveryLoading(true);

      const response = await api.post('/auth/forgot-password', { email });

      if (response.data.success) {
        setRecoveryMessage(response.data.message || 'Se ha enviado un enlace de recuperación a tu correo');
        setRecoveryError('');

        // Volver al login después de 5 segundos
        setTimeout(() => {
          setIsRecoveryMode(false);
          setRecoveryMessage('');
          setEmail('');
        }, 5000);
      } else {
        setRecoveryError(response.data.message || 'Error al enviar el correo. Intenta de nuevo.');
      }
    } catch (err) {
      console.error('Recovery error:', err);
      setRecoveryError(err.response?.data?.message || 'Error al enviar el correo. Intenta de nuevo.');
      setRecoveryMessage('');
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <Html
      transform
      distanceFactor={18}
      position={[0, 18, 35]}
      center
    >
      <div className="login3d-form-container">
        <h1 className="login3d-title">PAPER BOAT</h1>

        {isRecoveryMode ? (
          // Formulario de Recuperación
          <form onSubmit={handleRecovery} className="login3d-form">
            <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px', textAlign: 'center', marginBottom: '20px' }}>
              Ingresa tu correo electrónico y te enviaremos un enlace para recuperar tu contraseña
            </p>

            <div className="login3d-input-group">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login3d-input"
                disabled={recoveryLoading}
              />
              <div className="login3d-input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            </div>

            {recoveryError && (
              <div className="login3d-error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{recoveryError}</span>
                <button
                  onClick={() => setRecoveryError('')}
                  className="login3d-error-close"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            )}

            {recoveryMessage && (
              <div className="login3d-success">
                <span>{recoveryMessage}</span>
              </div>
            )}

            <button type="submit" className="login3d-button" disabled={recoveryLoading || recoveryMessage}>
              {recoveryLoading ? (
                <div className="login3d-spinner"></div>
              ) : recoveryMessage ? (
                <span>Enlace Enviado</span>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>Enviar Enlace</span>
                </>
              )}
            </button>

            <div className="login3d-forgot-password" style={{ textAlign: 'center', marginTop: '15px' }}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setIsRecoveryMode(false); setRecoveryError(''); setRecoveryMessage(''); }}
                style={{ color: 'rgba(255, 255, 255, 0.85)', textDecoration: 'none', fontSize: '14px' }}
              >
                ← Volver al Login
              </a>
            </div>
          </form>
        ) : (
          // Formulario de Login Normal
          <form onSubmit={handleSubmit} className="login3d-form">

          <div className="login3d-input-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login3d-input"
              disabled={loading}
            />
            <div className="login3d-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          </div>

          <div className="login3d-input-group">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="login3d-input"
              disabled={loading}
            />
            <div
              className="login3d-input-icon"
              onClick={() => setShowPassword(!showPassword)}
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </div>
          </div>

          {error && (
            <div className="login3d-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
              <button
                onClick={() => dispatch(clearError())}
                className="login3d-error-close"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}

          <div className="login3d-forgot-password">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setIsRecoveryMode(true); }}
              style={{ color: 'rgba(255, 255, 255, 0.85)', textDecoration: 'none', fontSize: '14px' }}
            >
              ¿Olvidaste tu contraseña?
            </a>
          </div>

          <button type="submit" className="login3d-button" disabled={loading}>
            {loading ? (
              <div className="login3d-spinner"></div>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path>
                  <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path>
                  <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path>
                </svg>
                <span>Iniciar Sesión</span>
              </>
            )}
          </button>
        </form>
        )}
      </div>
    </Html>
  );
}

// Componente de Luces
function Lights() {
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[10, 15, 5]}
        intensity={1.2}
        castShadow
        shadow-mapSize={[2048, 2048]}
        color="#fff5e6"
      />
      <hemisphereLight
        skyColor="#87CEEB"
        groundColor="#006994"
        intensity={0.5}
      />
      <pointLight position={[0, 10, 0]} intensity={0.4} color="#ffffff" />
    </>
  );
}

// Escena principal
function Scene({ onLogin, error, loading }) {
  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 14, 60]} fov={70} />
      <OrbitControls
        enableZoom={true}
        enablePan={true}
        target={[0, 12, 0]}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 2}
        minAzimuthAngle={-Math.PI / 2}
        maxAzimuthAngle={Math.PI / 2}
      />

      <Lights />

      {/* Ocean Model */}
      <Suspense fallback={null}>
        <Ocean />
      </Suspense>

      {/* Paper Boat Model */}
      <Suspense fallback={null}>
        <PaperBoat />
      </Suspense>

      {/* Login Form */}
      <LoginForm3D onLogin={onLogin} error={error} loading={loading} />
    </>
  );
}

// Componente principal Login3D
const Login3D = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated, isSuperAdmin } = useSelector(state => state.auth);

  useEffect(() => {
    // Limpiar datos de sesión al cargar el login (NO limpiar error)
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isSuperAdmin');
    localStorage.removeItem('empresaActiva');
    sessionStorage.clear();
  }, []);

  // NO limpiar error automáticamente - usuario debe cerrarlo manualmente

  useEffect(() => {
    if (isAuthenticated) {
      if (isSuperAdmin) {
        navigate('/super-admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, isSuperAdmin, navigate]);

  const handleLogin = ({ email, password }) => {
    dispatch(login({ email, password }));
  };

  return (
    <div className="login3d-container">
      <Canvas
        shadows
        dpr={[1, 2]}
        gl={{
          antialias: true,
          alpha: false,
          preserveDrawingBuffer: true
        }}
        style={{ background: '#000000' }}
      >
        <Scene onLogin={handleLogin} error={error} loading={loading} />
      </Canvas>

      <Loader
        containerStyles={{
          background: 'transparent'
        }}
        barStyles={{
          background: 'transparent'
        }}
        dataStyles={{
          color: 'transparent'
        }}
      />

      <a
        href="/dashboard"
        className="login3d-quick-nav"
        onClick={(e) => {
          e.preventDefault();
          navigate('/dashboard');
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
        <span>Vista Login</span>
      </a>

      <div className="login3d-footer">
        <p>© 2025 Paper Boat - Todos los derechos reservados</p>
      </div>
    </div>
  );
};

// Precargar los modelos
useGLTF.preload('/paper_boat.glb');

export default Login3D;